import {nanoid} from 'nanoid';
import {
  callGetUserIdForUserHash,
  postFacebookEvent,
  postMixpanelEvent,
} from './firebase';
import {
  getLocalStorageConfiguratorV2Data,
  getLocalStorageMixpanelId,
  getLocalStorageUid,
  setLocalStorageUid,
} from './localStorage';
import {ProductName} from './storeConfigurator';
import {getUserLocation} from './storeGeoLocator';

export enum GoogleEvents {
  ADD_TO_CART = 'add_to_cart',
  VIEW_ITEM = 'view_item',
  PAYMENT_STARTED = 'begin_checkout',
  PAYMENT_COMPLETE = 'purchase',
  WRAPPED_OPENED = 'wrapped_opened',
  WRAPPED_SHARED = 'wrapped_shared',
  WRAPPED_UPSELL_CLICKED = 'wrapped_upsell_clicked',
  WRAPPED_VISITOR_OPENED = 'wrapped_visitor_opened',
}

export enum FacebookEvents {
  VIEW_ITEM = 'ViewContent',
  ADD_TO_CART = 'AddToCart',
  PAYMENT_STARTED = 'InitiateCheckout',
  PAYMENT_COMPLETE = 'Purchase',
}

export enum MixpanelEvents {
  WEBSITE_ENTERED = 'website_entered',
  WHY_ORCA_ENTERED = 'why_orca_entered',
  PRODUCT_VIEWED = 'product_viewed',
  BLOG_HOME_ENTERED = 'blog_home_entered',
  BLOG_POST_ENTERED = 'blog_post_entered',
  ORCA_OWNERS_CLUB_CLICKED = 'orca_owners_club_clicked',
  CHAT_OPENED = 'chat_opened',
  CONTACT_ENTERED = 'contact_entered',
  CHART_COVERAGE_ENTERED = 'chart_coverage_entered',
  PRODUCT_QUANTITY_INCREASED = 'product_quantity_increased',
  PRODUCT_QUANTITY_DECREASED = 'product_quantity_decreased',
  PRODUCT_SELECTED = 'product_selected',
  PRODUCT_SKIPPED = 'product_skipped',
  CART_ENTERED = 'cart_entered',
  CART_CONTINUED = 'cart_continued',
  CHECKOUT_STARTED = 'checkout_started',
  PURCHASE_COMPLETED = 'purchase_completed',
  APP_DOWNLOAD_CLICKED = 'app_download_clicked',
  CALL_REQUEST_STARTED = 'call_request_started',
  VIEW_REVIEW_CLICKED = 'view_review_clicked',
  MY_ACCOUNT_ENTERED = 'my_account_entered',
  SOCIAL_MEDIA_CLICKED = 'social_media_clicked',
  SUPPORT_ENTERED = 'support_entered',
  PAGE_VIEWED = 'page_viewed',
  USER_IDENTIFIED = 'user_identified',
  COMMUNITY_CARD_CLICKED = 'community_card_clicked',
}

export enum ContentId {
  DISPLAY_2 = 'gm8sixxgd4',
  ORCA_CORE_2 = 'upayut7vmb',
}

export enum ItemId {
  SKU_DISPLAY_2 = 'SKU_DISPLAY_2',
  SKU_CORE_2 = 'SKU_CORE_2',
}

export enum ItemName {
  SKU_DISPLAY_2 = 'Orca Display 2',
  SKU_CORE_2 = 'Orca Core 2',
}

type GoogleParams = {
  currency?: string;
  value?: number;
  itemId?: ItemId;
};

type FacebookParams = {
  currency?: string;
  value?: number;
  content_ids?: string;
  content_type?: string;
  num_items?: number;
  stripeShippingInfo?: StripeShippingInfo;
};

export type MixpanelParams = {
  product_name?: ProductName;
  product_page_trigger_point?: string;
  blog_title?: string;
  slug?: string;
  chat_trigger_point?: string;
  cart_trigger_point?: string;
  value_euros?: number;
  app_download_trigger_point?: string;
  app_store_type?: string;
  review_destination?: string;
  social_media_type?: string;
  why_orca_trigger_point?: string;
  community_card_title?: string;
  community_card_publisher?: string;
};

export type StripeShippingInfo = {
  name: string;
  address: {
    city: string;
    country: string;
    line1: string;
    line2: string;
    postal_code: string;
  };
};

const getCookie = (identifier: string) => {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${identifier}=`);
  if (parts.length === 2) {
    const part = parts.pop();
    if (part) return part.split(';').shift();
  }
  return undefined;
};

const declareConversionValueForEvent = (event: GoogleEvents, value: number) => {
  switch (event) {
    case GoogleEvents.VIEW_ITEM:
      return value / 600;
    case GoogleEvents.ADD_TO_CART:
      return value / 300;
    case GoogleEvents.PAYMENT_STARTED:
      return value / 50;
    case GoogleEvents.PAYMENT_COMPLETE:
      return value;
    default:
      return 0;
  }
};

export const handleGoogleAnalytics = (
  event: GoogleEvents,
  params?: Partial<GoogleParams>
) => {
  if (window.gtag) {
    const id = nanoid(10);
    const items =
      params?.itemId && params?.currency && params?.value
        ? [
            {
              item_id: params.itemId,
              item_name: ItemName[params.itemId],
              currency: params.currency?.toUpperCase(),
              value: declareConversionValueForEvent(event, params.value),
              quantity: 1,
            },
          ]
        : undefined;

    window.gtag('event', event, {
      value: declareConversionValueForEvent(event, params?.value || 0),
      currency: params?.currency?.toUpperCase(),
      transaction_id: id,
      items,
    });
  }
};

const capitalizeWords = (str: string) =>
  str.replace(/\b\w/g, (char) => char.toUpperCase());

export const handleMixpanelAnalytics = async (
  event: MixpanelEvents,
  params: Partial<MixpanelParams> = {}
) => {
  try {
    const mixpanelId = getLocalStorageMixpanelId();
    const uid = getLocalStorageUid();
    const {city, country} = await getUserLocation();
    const capitalizedCity = capitalizeWords(city);
    const updatedParams = {
      ...params,
      mp_country_code: country,
      $city: capitalizedCity,
    };

    await postMixpanelEvent(event, mixpanelId, uid, updatedParams);
  } catch (error) {
    console.error('Mixpanel event logging failed:', error);
  }
};

export const handleFacebookAnalytics = (
  event: FacebookEvents,
  params?: Partial<FacebookParams>
) => {
  const id = nanoid(10);
  let fbc = getCookie('_fbc');
  const fbp = getCookie('_fbp');

  // Manually grab click IDs if we have them
  if (!fbc && window.location.search.includes('fbclid=')) {
    fbc = `fb.1.${+new Date()}.${window.location.search.split('fbclid=')[1]}`;
  }

  // Manually grab e-mail from localstorage
  const configuration = getLocalStorageConfiguratorV2Data();

  const paramsWithEmail = {
    ...params,
    email: configuration?.email,
  };

  if (window.fbq) {
    window.fbq(
      'track',
      event,
      {
        currency: paramsWithEmail.currency,
        value: paramsWithEmail.value,
        content_ids: paramsWithEmail.content_ids,
        num_items: paramsWithEmail.num_items,
        em: paramsWithEmail.email,
      },
      {
        eventID: id,
      }
    );
  }

  // We don't care about ViewContent for backend logging
  if (event === FacebookEvents.VIEW_ITEM) return;

  postFacebookEvent(event, paramsWithEmail, id, fbc, fbp);
};

export const handleSetUserId = (userId: string, email?: string) => {
  if (!userId) return;

  if (window.gtag) {
    window.gtag('config', 'GA_MEASUREMENT_ID', {
      user_id: userId,
    });
  }

  const fbqPayload = email
    ? {
        em: email,
        external_id: userId,
      }
    : {
        external_id: userId,
      };

  if (window.fbq) {
    window.fbq('init', '2818427685147135', fbqPayload);
  }

  // Set also uid to localStorage, to be used for mixpanel
  setLocalStorageUid(userId);
  handleMixpanelAnalytics(MixpanelEvents.USER_IDENTIFIED);
};

export const handleSetUserIdFromHash = async (userHash: string) => {
  const res = await callGetUserIdForUserHash(userHash);

  if (!res.uid) return;

  handleSetUserId(res.uid);
};

export const checkUserIdFromUrlQueryParam = () => {
  const urlParams = new URLSearchParams(window.location.search);
  const userId = urlParams.get('user_id');
  const userHash = urlParams.get('user_hash');

  if (userId && userId.length === 28) {
    handleSetUserId(userId);
  }

  if (userHash && userHash.length === 64) {
    handleSetUserIdFromHash(userHash);
  }
};
